import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  AppError,
  Avatar,
  Badge,
  Button,
  categoricalSlotByKey,
  type CategoricalSlot,
  EmptyState,
  Loader,
  Modal,
  Search,
  Select,
  selectState as ss,
  SidebarOverlay,
  Tag,
  TextInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  toast,
  ToggleChip,
  type SelectMsg,
  type SelectState,
} from "@bfc-software/pallet";
import { match } from "ts-pattern";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { AuthContext, AuthUtils, logger } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/aaa-api-client";
import { useIssApi } from "@bfc-software/bfc-web-ui/lib/iss-api-client";
import {
  useOrgCfgFetchClient,
  type OrgCfgFetchClient,
} from "@bfc-software/bfc-web-ui/lib/orgcfg-api-client";
import { DateValue } from "@bfc-software/bfc-web-ui/ui/shared/date-value";

/**
 * Matches `selectorAppName` in nextgen_config's selectorsHandler.go -- app
 * users are shared across every app on a warehouse, and this is what scopes
 * the list to the ones that sign in on the floor.
 */
const APP_NAME = "select";

const PIN_LENGTH = 6;

/**
 * Option values double as what gets persisted to `/orgs/user_settings/add` --
 * org_config.yaml defines the `settings` map as free-form strings with no
 * declared value enum, so these are this page's own canonical values rather
 * than anything the API prescribes.
 */
const TASK_TYPE_OPTIONS: ss.NonEmptyArray<string> = [
  "All",
  "Runner only",
  "Regular only",
];
const BAND_OPTIONS: ss.NonEmptyArray<string> = ["B1", "B2", "B3", "B4", "B5"];
const VALIDATION_OPTIONS: ss.NonEmptyArray<string> = ["Standard", "Enhanced"];
const ASSIGNMENT_OPTIONS: ss.NonEmptyArray<string> = [
  "System-assigned",
  "Manager-override",
];

/**
 * The table's own per-value slot for the Validation badge -- "Enhanced" gets
 * the categorical slot that matches the retired `VALIDATION_COLORS.Enhanced`
 * hex (`#7b61c9`, the ramp's 4th entry); "Standard" and anything unknown
 * fall back to slot 1 rather than the retired ad hoc `#6b7280` grey.
 */
const VALIDATION_SLOTS = new Map<string, CategoricalSlot>([["Enhanced", 4]]);

function validationSlot(value: string): CategoricalSlot {
  return categoricalSlotByKey(value, VALIDATION_SLOTS, 1);
}

const SUBSTITUTION_OPTIONS: ss.NonEmptyArray<string> = ["No", "Yes"];

/**
 * The `zone_eligibility` key of the `selectorSettings` enum (org_config.yaml)
 * -- unlike the other settings keys, its value is a set (a selector can be
 * eligible for more than one zone), so it's stored as a comma-joined list of
 * zone names rather than one of a fixed set of single values.
 */
const ZONE_ELIGIBILITY_KEY = "zone_eligibility";

function encodeZoneEligibility(zones: string[]): string {
  return zones.join(",");
}

function decodeZoneEligibility(value: string | undefined): string[] {
  return value ? value.split(",").filter(Boolean) : [];
}

/**
 * The `shift` key of the `selectorSettings` enum (org_config.yaml) -- unlike
 * every other key here, it has no fixed option set of its own: the choices
 * are whatever `Shifts` on the System Controls page has named for this
 * warehouse, and it has no default value, so an unassigned selector's
 * dropdown starts empty rather than seeded to one of them.
 */
const SHIFT_KEY = "shift";

type Choice = ss.Item;

function choice(id: string, label: string = id): Choice {
  return { id, label };
}

/** The same string -> row mapping the old `selectItems` did, but non-empty so `ss.fixed` accepts it. */
function choicesOf([
  first,
  ...rest
]: ss.NonEmptyArray<string>): ss.NonEmptyArray<Choice> {
  return [choice(first), ...rest.map((o) => choice(o))];
}

const TASK_TYPE_CHOICES = choicesOf(TASK_TYPE_OPTIONS);
const BAND_CHOICES = choicesOf(BAND_OPTIONS);
const VALIDATION_CHOICES = choicesOf(VALIDATION_OPTIONS);
const ASSIGNMENT_CHOICES = choicesOf(ASSIGNMENT_OPTIONS);
const SUBSTITUTION_CHOICES = choicesOf(SUBSTITUTION_OPTIONS);

/**
 * Pure. A required value over a static list (`always`, no search): the
 * dropdowns that are never empty. No lookup and no fallback: the caller builds
 * `selected` from the value it holds, and it need not be a member of `items`.
 * That matters in the drawer, where the value is seeded from the service
 * (`settings.task_type_eligibility || "All"`) and could be a string this page
 * doesn't list; the trigger still names it.
 */
function choiceState(a: {
  label: string; // visible, column
  items: ss.NonEmptyArray<Choice>;
  selected: Choice;
}): SelectState<Choice> {
  return ss.always({
    label: ss.visibleLabel({ text: a.label, orientation: "column" }),
    options: ss.fixed<Choice>(a.items),
    selected: a.selected,
  });
}

/** One handler shape for every `choiceState` site; only the setter differs. `always`: only `picked` can arrive. */
const choiceMsg = (set: (value: string) => void) => (msg: SelectMsg<Choice>) =>
  match(msg)
    .with({ kind: "picked" }, ({ item }) => set(String(item.id)))
    .otherwise(() => {});

/** Pure. `shift` is optional (`""` = none), so `single` (shows the `x`); `ready` because the list may be `[]`. */
function shiftState(a: {
  label: string;
  placeholder: string;
  emptyLabel: string;
  shiftNames: string[];
  value: string;
}): SelectState<Choice> {
  const base = ss.single<Choice>({
    label: ss.visibleLabel({ text: a.label, orientation: "column" }),
    placeholder: a.placeholder,
    options: ss.ready({
      items: a.shiftNames.map((n) => choice(n)),
      emptyLabel: a.emptyLabel,
    }),
  });
  // `value` goes in the selection, not looked up, so a since-deleted shift still names itself
  return a.value === "" ? base : ss.withPick(base, choice(a.value));
}

const shiftMsg = (set: (value: string) => void) => (msg: SelectMsg<Choice>) =>
  match(msg)
    .with({ kind: "picked" }, ({ item }) => set(String(item.id)))
    .with({ kind: "cleared" }, () => set(""))
    .otherwise(() => {}); // no search; `opened`/`closed` carry nothing here

/** The zone-eligibility multi-select -- shared by the create form and the edit drawer. */
function ZonePillPicker({
  zoneNames,
  selected,
  onToggle,
  label,
  emptyLabel,
}: {
  zoneNames: string[];
  selected: string[];
  onToggle: (zoneName: string) => void;
  label: string;
  emptyLabel: string;
}) {
  return (
    <div>
      <span className="mb-8 block text-size-13 font-strong text-on-surface-variant">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-8">
        {zoneNames.map((zoneName) => {
          const isSelected = selected.includes(zoneName);
          return (
            <ToggleChip
              key={zoneName}
              label={zoneName}
              isSelected={isSelected}
              onChange={() => onToggle(zoneName)}
            />
          );
        })}
        {zoneNames.length === 0 ? (
          <p className="mt-4 text-size-12 text-on-surface-variant">
            {emptyLabel}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/selectors",
)({
  component: RouteComponent,
});

type AppUser = {
  id: number;
  user_id: string;
  user_name?: string;
  active: boolean;
  created_at: string;
};

function generatePin(): string {
  return String(Math.floor(100000 + Math.random() * 900000));
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, "").slice(0, PIN_LENGTH);
}

/** AAA's `user_name` is optional -- a selector created before it existed, or without one set, falls back to the login id. */
function displayNameOf(user: AppUser): string {
  return user.user_name || user.user_id;
}

/**
 * openapi-react-query throws the parsed apiResponse body itself
 * (`{ code, message }`) rather than an `Error` -- see the analogous helper in
 * items.tsx.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { message?: unknown; error?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
    if (typeof body.error === "string" && body.error) return body.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Shared by the row-level "User group"/"Validation" columns and the detail
 * drawer's own settings section -- both need the same per-user settings map,
 * and the row and drawer end up sharing one cache entry (same query key) once
 * both have been fetched for a given selector.
 */
function useSelectorSettings(
  orgId: string,
  warehouseId: string,
  userId: string,
) {
  const client = useOrgCfgFetchClient();
  return useQuery({
    queryKey: ["selector-settings", orgId, warehouseId, userId],
    queryFn: async () => {
      const { data, response } = await client.GET("/orgs/user_settings/get", {
        params: { query: { orgId, warehouseId, userId } },
      });
      // A selector who has never had settings saved gets a 404 here -- that
      // is an empty settings map, not a failure.
      if (response.status === 404) return {};
      if (!data) throw new Error("Failed to load selector settings");
      return data.settings ?? {};
    },
  });
}

/**
 * The warehouse's own shift names (System Controls' `Shifts` card), for the
 * `shift` dropdown's option list -- this page only reads that record, it
 * never writes to it. `appWarehouseSettings.settings` is typed as
 * `selectWarehouseSettings | deliverWarehouseSettings` (org_config.yaml's
 * `warehouseSettings` oneOf), which is why the `shiftList` read below is
 * narrowed with an `in` check the same way system-controls'
 * `warehouse-settings-client.ts` narrows it -- this app name is always
 * "select", so a response missing `shiftList` means an empty option list
 * rather than a failure worth surfacing.
 */
function useWarehouseShiftNames(
  orgId: string,
  warehouseId: string,
  enabled: boolean,
) {
  const client = useOrgCfgFetchClient();
  return useQuery({
    queryKey: ["warehouse-shift-names", orgId, warehouseId],
    queryFn: async () => {
      const { data, error } = await client.GET("/orgs/warehouse_settings/get", {
        params: { query: { orgId, warehouseId, appName: APP_NAME } },
      });
      if (error || !data)
        throw error ?? new Error("Failed to load warehouse shifts");
      const settings = data.settings;
      if (!settings || !("shiftList" in settings)) return [];
      return settings.shiftList.map((shift) => shift.name);
    },
    enabled,
  });
}

/**
 * Merges `patch` onto the selector's current settings and persists it.
 * Fetches the current settings first rather than trusting whatever the
 * caller already had in memory -- an `update` against a selector with no
 * settings record yet 404s, so a missing record falls back to `add`, and
 * merging (rather than overwriting) means this can save just the fields an
 * editing form owns without clobbering keys it doesn't know about (e.g.
 * `group`, which is only ever written from the User Groups page).
 */
async function saveSelectorSettings(
  client: OrgCfgFetchClient,
  orgId: string,
  warehouseId: string,
  userId: string,
  patch: Record<string, string>,
): Promise<void> {
  const { data, response } = await client.GET("/orgs/user_settings/get", {
    params: { query: { orgId, warehouseId, userId } },
  });
  const exists = response.status !== 404;
  const nextSettings: Record<string, string> = {
    ...(exists ? data?.settings : {}),
    ...patch,
  };

  const { error } = await client.POST(
    exists ? "/orgs/user_settings/update" : "/orgs/user_settings/add",
    { body: { orgId, warehouseId, userId, settings: nextSettings } },
  );
  if (error) throw error;
}

/**
 * A PIN this page's own form just set is known here in plaintext for exactly
 * one render -- create and reset-PIN both end in this one-time reveal of the
 * value that was just submitted, not a value read back from the server.
 */
function PinRevealPanel({
  userId,
  pin,
  body,
  doneLabel,
  onDone,
  t,
}: {
  userId: string;
  pin: string;
  body: string;
  doneLabel: string;
  onDone: () => void;
  t: TFunction;
}) {
  return (
    <div className="flex flex-col">
      <p className="mb-16 text-size-13 text-on-surface-variant">{body}</p>
      <div className="mt-4 mb-20 grid grid-cols-2 gap-12 rounded-8 bg-surface-container py-12 px-16">
        <div>
          <span className="block section-heading">
            {t("selectors.table.userId")}
          </span>
          <span className="block text-size-16 text-on-surface">{userId}</span>
        </div>
        <div>
          <span className="block section-heading">
            {t("selectors.create.pinLabel")}
          </span>
          <span className="block font-mono text-size-16 text-on-surface">
            {pin}
          </span>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-8">
        <Button
          kind="text-only"
          variant="avatar-surface"
          text={doneLabel}
          onPress={onDone}
        />
      </div>
    </div>
  );
}

/** What `handleCreate` needs beyond the AAA account itself -- see its own comment for why these are best-effort follow-ups rather than part of the account-creation failure path. */
type CreateSelectorExtras = {
  name: string;
  active: boolean;
  settings: Record<string, string>;
};

function CreateSelectorModal({
  isOpen,
  onOpenChange,
  onCreate,
  isPending,
  zoneNames,
  shiftNames,
  t,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (
    userId: string,
    pin: string,
    extras: CreateSelectorExtras,
  ) => Promise<void>;
  isPending: boolean;
  zoneNames: string[];
  shiftNames: string[];
  t: TFunction;
}) {
  const [name, setName] = useState("");
  const [userId, setUserId] = useState("");
  const [pin, setPin] = useState("");
  const [active, setActive] = useState(true);
  const [zoneEligibility, setZoneEligibility] = useState<string[]>([]);
  const [taskType, setTaskType] = useState("All");
  const [band, setBand] = useState("B3");
  const [validationLevel, setValidationLevel] = useState("Standard");
  const [assignment, setAssignment] = useState("System-assigned");
  const [substitution, setSubstitution] = useState("No");
  // No default, unlike every field above: `shift` only means something once
  // System Controls has named at least one, so a fresh selector starts
  // unassigned rather than seeded to one of them.
  const [shift, setShift] = useState("");
  const [created, setCreated] = useState<{
    userId: string;
    pin: string;
  } | null>(null);

  const toggleZone = (zoneName: string) => {
    setZoneEligibility((prev) =>
      prev.includes(zoneName)
        ? prev.filter((z) => z !== zoneName)
        : [...prev, zoneName],
    );
  };

  const reset = () => {
    setName("");
    setUserId("");
    setPin("");
    setActive(true);
    setZoneEligibility([]);
    setTaskType("All");
    setBand("B3");
    setValidationLevel("Standard");
    setAssignment("System-assigned");
    setSubstitution("No");
    setShift("");
    setCreated(null);
  };

  // Routed through this rather than `onOpenChange` directly so the Cancel/Done
  // buttons reset state exactly like an Escape-key or backdrop dismissal does
  // -- otherwise a stale `created` (or any other field) from the last time
  // this modal was open survives and reappears the next time it opens.
  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) reset();
  };

  const close = () => handleOpenChange(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedId = userId.trim();
    if (isPending || !trimmedId || pin.length !== PIN_LENGTH) return;

    try {
      await onCreate(trimmedId, pin, {
        name: name.trim(),
        active,
        settings: {
          [ZONE_ELIGIBILITY_KEY]: encodeZoneEligibility(zoneEligibility),
          task_type_eligibility: taskType,
          difficulty_band_cap: band,
          validation_level: validationLevel,
          assignment,
          substitution_authorized: substitution,
          // Omitted rather than sent empty when nobody picked one: `shift`
          // has no default, so a value here should only ever be one the
          // admin actually chose.
          ...(shift ? { [SHIFT_KEY]: shift } : {}),
        },
      });
      setCreated({ userId: trimmedId, pin });
    } catch {
      // onCreate already reported the failure via toast; keep the form open
      // so the admin can retry without re-entering values.
    }
  };

  return (
    <Modal
      control="external"
      data-test-role="create-selector-modal"
      isDismissable={!isPending}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      width="standard"
      header={
        <h2 className="mb-4 text-size-18 text-on-surface">
          {created
            ? t("selectors.create.successTitle")
            : t("selectors.create.title")}
        </h2>
      }
    >
      {/* `Modal` never caps a tall body's height (see the `width` prop
          comment in modal.tsx: the plate is shrink-to-fit unless a caller
          opts into a fixed width), and a viewport-relative length has no
          utility, so the cap is an inline `style` here. */}
      <div
        className="flex max-w-full flex-col overflow-y-auto"
        // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
        style={{ maxHeight: "calc(100vh - 320px)" }}
      >
        {created ? (
          <PinRevealPanel
            userId={created.userId}
            pin={created.pin}
            body={t("selectors.create.successBody", {
              userId: created.userId,
            })}
            doneLabel={t("selectors.create.done")}
            onDone={close}
            t={t}
          />
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-12">
            <p className="mb-16 text-size-13 text-on-surface-variant">
              {t("selectors.create.intro")}
            </p>
            <TextInput
              name="name"
              label={t("selectors.create.nameLabel")}
              placeholder={t("selectors.create.namePlaceholder")}
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              autofocus={true}
            />
            <div className="flex gap-12">
              <TextInput
                className="min-w-0 flex-1"
                name="user-id"
                label={t("selectors.create.userIdLabel")}
                placeholder={t("selectors.create.userIdPlaceholder")}
                value={userId}
                onChange={(ev) => setUserId(ev.target.value)}
                isRequired={true}
              />
              <Select
                className="min-w-0 flex-1"
                state={choiceState({
                  label: t("selectors.create.statusLabel"),
                  items: [
                    choice("active", t("selectors.status.active")),
                    choice("inactive", t("selectors.status.inactive")),
                  ],
                  selected: choice(
                    active ? "active" : "inactive",
                    t(
                      active
                        ? "selectors.status.active"
                        : "selectors.status.inactive",
                    ),
                  ),
                })}
                onMsg={choiceMsg((v) => setActive(v === "active"))}
              />
            </div>
            <div className="flex items-end gap-8">
              <TextInput
                className="flex-1"
                name="pin"
                label={t("selectors.create.pinLabel")}
                placeholder={t("selectors.create.pinPlaceholder")}
                value={pin}
                onChange={(ev) => setPin(digitsOnly(ev.target.value))}
                isRequired={true}
                maxLength={PIN_LENGTH}
              />
              <Button
                kind="text-only"
                type="button"
                variant="secondary"
                text={t("selectors.create.generate")}
                onPress={() => setPin(generatePin())}
              />
            </div>

            <div className="border-t border-outline-variant" />
            <div className="section-heading">
              {t("selectors.create.settingsHeading")}
            </div>

            <ZonePillPicker
              zoneNames={zoneNames}
              selected={zoneEligibility}
              onToggle={toggleZone}
              label={t("selectors.detail.zoneEligibility")}
              emptyLabel={t("selectors.create.noZones")}
            />

            <div className="flex gap-12">
              <Select
                className="min-w-0 flex-1"
                state={choiceState({
                  label: t("selectors.detail.taskTypeEligibility"),
                  items: TASK_TYPE_CHOICES,
                  selected: choice(taskType),
                })}
                onMsg={choiceMsg(setTaskType)}
              />
              <Select
                className="min-w-0 flex-1"
                state={choiceState({
                  label: t("selectors.detail.difficultyBandCap"),
                  items: BAND_CHOICES,
                  selected: choice(band),
                })}
                onMsg={choiceMsg(setBand)}
              />
            </div>
            <div className="flex gap-12">
              <Select
                className="min-w-0 flex-1"
                state={choiceState({
                  label: t("selectors.detail.validationLevel"),
                  items: VALIDATION_CHOICES,
                  selected: choice(validationLevel),
                })}
                onMsg={choiceMsg(setValidationLevel)}
              />
              <Select
                className="min-w-0 flex-1"
                state={choiceState({
                  label: t("selectors.detail.assignment"),
                  items: ASSIGNMENT_CHOICES,
                  selected: choice(assignment),
                })}
                onMsg={choiceMsg(setAssignment)}
              />
            </div>
            <div className="flex gap-12">
              <Select
                className="min-w-0 flex-1"
                state={choiceState({
                  label: t("selectors.detail.substitutionAuthorized"),
                  items: SUBSTITUTION_CHOICES,
                  selected: choice(substitution),
                })}
                onMsg={choiceMsg(setSubstitution)}
              />
              <Select
                className="min-w-0 flex-1"
                state={shiftState({
                  label: t("selectors.detail.shift"),
                  placeholder: t("selectors.detail.shiftPlaceholder"),
                  emptyLabel: t("selectors.detail.noShifts"),
                  shiftNames,
                  value: shift,
                })}
                onMsg={shiftMsg(setShift)}
              />
            </div>

            <div className="mt-4 flex justify-end gap-8">
              <Button
                kind="text-only"
                variant="avatar-surface"
                type="submit"
                text={
                  isPending
                    ? t("selectors.create.submitting")
                    : t("selectors.create.submit")
                }
                isDisabled={
                  isPending || !userId.trim() || pin.length !== PIN_LENGTH
                }
              />
              <Button
                kind="text-only"
                type="button"
                variant="tertiary"
                text={t("selectors.create.cancel")}
                onPress={close}
                isDisabled={isPending}
              />
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

function ResetPinModal({
  user,
  onOpenChange,
  onSubmit,
  isPending,
  t,
}: {
  user: AppUser | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (pin: string) => Promise<void>;
  isPending: boolean;
  t: TFunction;
}) {
  const [pin, setPin] = useState("");
  const [resetPin, setResetPin] = useState<string | null>(null);

  // Routed through this rather than `onOpenChange` directly so the
  // Cancel/Done buttons reset state exactly like an Escape-key or backdrop
  // dismissal does -- otherwise a stale `resetPin` from the last time this
  // modal was open survives and reappears the next time it opens.
  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      setPin("");
      setResetPin(null);
    }
  };

  const close = () => handleOpenChange(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isPending || pin.length !== PIN_LENGTH) return;

    try {
      await onSubmit(pin);
      setResetPin(pin);
    } catch {
      // onSubmit already reported the failure via toast; keep the form open.
    }
  };

  return (
    <Modal
      control="external"
      data-test-role="reset-pin-modal"
      isDismissable={!isPending}
      isOpen={user !== null}
      onOpenChange={handleOpenChange}
      width="standard"
      header={
        <h2 className="mb-4 text-size-18 text-on-surface">
          {user && resetPin
            ? t("selectors.resetPin.successTitle")
            : t("selectors.resetPin.title")}
        </h2>
      }
    >
      <div
        className="flex max-w-full flex-col overflow-y-auto"
        // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
        style={{ maxHeight: "calc(100vh - 320px)" }}
      >
        {user && resetPin ? (
          <PinRevealPanel
            userId={user.user_id}
            pin={resetPin}
            body={t("selectors.resetPin.successBody", {
              userId: user.user_id,
            })}
            doneLabel={t("selectors.resetPin.done")}
            onDone={close}
            t={t}
          />
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-12">
            <p className="mb-16 text-size-13 text-on-surface-variant">
              {user
                ? t("selectors.resetPin.intro", { userId: user.user_id })
                : ""}
            </p>
            <div className="flex items-end gap-8">
              <TextInput
                className="flex-1"
                name="new-pin"
                label={t("selectors.resetPin.pinLabel")}
                placeholder={t("selectors.create.pinPlaceholder")}
                value={pin}
                onChange={(ev) => setPin(digitsOnly(ev.target.value))}
                isRequired={true}
                maxLength={PIN_LENGTH}
                autofocus={true}
              />
              <Button
                kind="text-only"
                type="button"
                variant="secondary"
                text={t("selectors.resetPin.generate")}
                onPress={() => setPin(generatePin())}
              />
            </div>
            <div className="mt-4 flex justify-end gap-8">
              <Button
                kind="text-only"
                variant="avatar-surface"
                type="submit"
                text={
                  isPending
                    ? t("selectors.resetPin.submitting")
                    : t("selectors.resetPin.submit")
                }
                isDisabled={isPending || pin.length !== PIN_LENGTH}
              />
              <Button
                kind="text-only"
                type="button"
                variant="tertiary"
                text={t("selectors.resetPin.cancel")}
                onPress={close}
                isDisabled={isPending}
              />
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

/**
 * `group` is deliberately left out of the editable form below -- it's only
 * ever written from the User Groups page (so zones/users on the group side
 * stay in sync with it), so this drawer surfaces it read-only next to the
 * other identity fields instead.
 */
function SelectorDetailDrawer({
  user,
  orgId,
  warehouseId,
  zoneNames,
  shiftNames,
  onClose,
  onSave,
  isSaving,
  t,
}: {
  user: AppUser;
  orgId: string;
  warehouseId: string;
  zoneNames: string[];
  shiftNames: string[];
  onClose: () => void;
  onSave: (userId: string, patch: Record<string, string>) => Promise<void>;
  isSaving: boolean;
  t: TFunction;
}) {
  const statusLabel = user.active
    ? t("selectors.status.active")
    : t("selectors.status.inactive");

  const settingsQuery = useSelectorSettings(orgId, warehouseId, user.user_id);

  const settings = settingsQuery.data ?? {};

  const [initialized, setInitialized] = useState(false);
  const [zoneEligibility, setZoneEligibility] = useState<string[]>([]);
  const [taskType, setTaskType] = useState("All");
  const [band, setBand] = useState("B3");
  const [validationLevel, setValidationLevel] = useState("Standard");
  const [assignment, setAssignment] = useState("System-assigned");
  const [substitution, setSubstitution] = useState("No");
  // No fallback, unlike the fields above: an existing selector with no
  // `shift` saved yet (every selector created before this field existed,
  // or one nobody has assigned) starts unassigned rather than seeded to
  // whatever the first shift in the list happens to be.
  const [shift, setShift] = useState("");

  // Seeds the form from the fetched settings exactly once -- after that, the
  // fields are this form's own state, so a background refetch (e.g. after
  // save) doesn't stomp on what the admin is mid-way through editing.
  useEffect(() => {
    if (initialized || !settingsQuery.isSuccess) return;
    setZoneEligibility(decodeZoneEligibility(settings[ZONE_ELIGIBILITY_KEY]));
    setTaskType(settings.task_type_eligibility || "All");
    setBand(settings.difficulty_band_cap || "B3");
    setValidationLevel(settings.validation_level || "Standard");
    setAssignment(settings.assignment || "System-assigned");
    setSubstitution(settings.substitution_authorized || "No");
    setShift(settings[SHIFT_KEY] || "");
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once, see comment above
  }, [initialized, settingsQuery.isSuccess]);

  const toggleZone = (zoneName: string) => {
    setZoneEligibility((prev) =>
      prev.includes(zoneName)
        ? prev.filter((z) => z !== zoneName)
        : [...prev, zoneName],
    );
  };

  const handleSave = async () => {
    try {
      await onSave(user.user_id, {
        [ZONE_ELIGIBILITY_KEY]: encodeZoneEligibility(zoneEligibility),
        task_type_eligibility: taskType,
        difficulty_band_cap: band,
        validation_level: validationLevel,
        assignment,
        substitution_authorized: substitution,
        // Omitted rather than sent empty when nobody's picked one -- see the
        // create form's own comment on `SHIFT_KEY`.
        ...(shift ? { [SHIFT_KEY]: shift } : {}),
      });
    } catch {
      // onSave already reported the failure via toast; keep the drawer open
      // so the admin can retry without re-entering values.
    }
  };

  return (
    // `isOpen` is a constant for the same reason it is in `items.tsx`'s
    // `ItemDrawer`: the caller renders this only for a selected selector, so
    // that selection is the single answer to "is it open" and `onOpenChange`
    // routes every dismissal back to it.
    <SidebarOverlay
      control="external"
      isOpen={true}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
      // The avatar and the status chip beside the name are this screen's, not
      // chrome `SidebarOverlay` could supply, so they compose into `header`.
      header={
        <div className="flex items-center gap-16">
          <Avatar
            name={displayNameOf(user)}
            colorKey={user.user_id}
            size="lg"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <div className="flex flex-wrap items-center gap-8">
              <h2 className="drawer-title">{displayNameOf(user)}</h2>
              <Tag
                label={statusLabel}
                display={{ tone: user.active ? "success" : "neutral" }}
              />
            </div>
            <span className="drawer-subtitle">
              {t("selectors.detail.subtitleRole")} ·{" "}
              <DateValue dateValue={user.created_at} />
            </span>
          </div>
        </div>
      }
    >
      <div className="drawer-body">
        <div className="section gap-8">
          <div className="section-heading">
            {t("selectors.detail.identityHeading")}
          </div>
          <dl className="detail-grid">
            <div>
              <dt>{t("selectors.table.userId")}</dt>
              <dd>{user.user_id}</dd>
            </div>
            <div>
              <dt>{t("selectors.table.status")}</dt>
              <dd>{statusLabel}</dd>
            </div>
            <div>
              <dt>{t("selectors.table.created")}</dt>
              <dd>
                <DateValue dateValue={user.created_at} />
              </dd>
            </div>
            <div>
              <dt>{t("selectors.table.userGroup")}</dt>
              <dd>
                {settings.group || (
                  <Tag
                    label={t("selectors.table.notAssigned")}
                    display={{ tone: "warning" }}
                  />
                )}
              </dd>
            </div>
          </dl>
        </div>

        <div className="section gap-8">
          <div className="section-heading">
            {t("selectors.detail.settingsHeading")}
          </div>
          {settingsQuery.isLoading ? (
            <Loader type="inline" />
          ) : settingsQuery.isError ? (
            <p className="text-size-13 text-on-surface-variant">
              {t("selectors.detail.settingsFailed")}
            </p>
          ) : (
            <div className="flex flex-col gap-12">
              <ZonePillPicker
                zoneNames={zoneNames}
                selected={zoneEligibility}
                onToggle={toggleZone}
                label={t("selectors.detail.zoneEligibility")}
                emptyLabel={t("selectors.create.noZones")}
              />
              <div className="flex gap-12">
                <Select
                  className="min-w-0 flex-1"
                  state={choiceState({
                    label: t("selectors.detail.taskTypeEligibility"),
                    items: TASK_TYPE_CHOICES,
                    selected: choice(taskType),
                  })}
                  onMsg={choiceMsg(setTaskType)}
                />
                <Select
                  className="min-w-0 flex-1"
                  state={choiceState({
                    label: t("selectors.detail.difficultyBandCap"),
                    items: BAND_CHOICES,
                    selected: choice(band),
                  })}
                  onMsg={choiceMsg(setBand)}
                />
              </div>
              <div className="flex gap-12">
                <Select
                  className="min-w-0 flex-1"
                  state={choiceState({
                    label: t("selectors.detail.validationLevel"),
                    items: VALIDATION_CHOICES,
                    selected: choice(validationLevel),
                  })}
                  onMsg={choiceMsg(setValidationLevel)}
                />
                <Select
                  className="min-w-0 flex-1"
                  state={choiceState({
                    label: t("selectors.detail.assignment"),
                    items: ASSIGNMENT_CHOICES,
                    selected: choice(assignment),
                  })}
                  onMsg={choiceMsg(setAssignment)}
                />
              </div>
              <div className="flex gap-12">
                <Select
                  className="min-w-0 flex-1"
                  state={choiceState({
                    label: t("selectors.detail.substitutionAuthorized"),
                    items: SUBSTITUTION_CHOICES,
                    selected: choice(substitution),
                  })}
                  onMsg={choiceMsg(setSubstitution)}
                />
                <Select
                  className="min-w-0 flex-1"
                  state={shiftState({
                    label: t("selectors.detail.shift"),
                    placeholder: t("selectors.detail.shiftPlaceholder"),
                    emptyLabel: t("selectors.detail.noShifts"),
                    shiftNames,
                    value: shift,
                  })}
                  onMsg={shiftMsg(setShift)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="drawer-footer">
        <Button
          kind="text-only"
          variant="avatar-surface"
          type="button"
          text={
            isSaving ? t("selectors.detail.saving") : t("selectors.detail.save")
          }
          onPress={handleSave}
          isDisabled={
            isSaving || settingsQuery.isLoading || settingsQuery.isError
          }
        />
        <Button
          kind="text-only"
          type="button"
          variant="tertiary"
          text={t("selectors.detail.cancel")}
          onPress={onClose}
          isDisabled={isSaving}
        />
      </div>
    </SidebarOverlay>
  );
}

type TeamQrCode = { userId: string; dataUrl: string };

/**
 * Mirrors nextgen_config's `printQRCodes()` -- a print-only copy of the grid
 * rendered straight into `document.body` (outside the modal's overlay/portal
 * stack) so `window.print()` has something printable to find: the overlay
 * itself is `position: fixed` and clipped/scrolling, which prints as a blank
 * or cut-off page in every browser we support. This copy stays hidden except
 * under `@media print`, where the rest of the page is hidden in its place.
 *
 * Hiding every OTHER element on the page while this one prints can be
 * neither a utility class nor a component prop -- it reaches siblings outside
 * this component's own subtree -- so it is a literal `<style>` tag scoped to
 * this portal.
 */
function TeamQrPrintArea({ qrCodes }: { qrCodes: TeamQrCode[] }) {
  return createPortal(
    <>
      <style>{`
        @media print {
          body > *:not([data-test-role="team-qr-print-area"]) {
            display: none !important;
          }
        }
      `}</style>
      <div
        data-test-role="team-qr-print-area"
        className="hidden p-16 print:block"
      >
        <div className="grid grid-cols-3 gap-16">
          {qrCodes.map((qrCode) => (
            <div
              key={qrCode.userId}
              className="flex flex-col items-center gap-4 break-inside-avoid rounded-8 border border-outline bg-surface p-12"
            >
              {/* A fixed image dimension, not a layout gap: pallet's
                  spacing scale tops out at 32, so this is an inline `style`. */}
              <img
                className="block"
                // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
                style={{ width: 140, height: 140 }}
                src={qrCode.dataUrl}
                alt=""
              />
              <span className="font-mono text-size-11 font-strong text-on-surface">
                {qrCode.userId}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>,
    document.body,
  );
}

/**
 * Fetches every selector's QR code image (already rendered server-side by
 * orgcfg's `/orgs/user_groups/qr_codes`) and renders a printable grid --
 * no PIN material ever reaches this page, just the finished images.
 */
function TeamQrCodesModal({
  isOpen,
  onClose,
  orgId,
  warehouseId,
  t,
}: {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  warehouseId: string;
  t: TFunction;
}) {
  const client = useOrgCfgFetchClient();
  const qrQuery = useQuery({
    queryKey: ["team-qr-codes", orgId, warehouseId],
    queryFn: async () => {
      const { data } = await client.GET("/orgs/user_groups/qr_codes", {
        params: { query: { orgId, warehouseId, appName: APP_NAME } },
      });
      if (!data) throw new Error("Failed to load team QR codes");
      return data.qrCodes;
    },
    enabled: isOpen,
  });

  const qrCodes = qrQuery.data ?? [];

  return (
    <>
      {isOpen ? <TeamQrPrintArea qrCodes={qrCodes} /> : null}
      <Modal
        control="external"
        data-test-role="team-qr-modal"
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        width="wide"
        header={
          <div className="flex items-center gap-12">
            <h2 className="text-size-18 text-on-surface">
              {t("selectors.teamQr.title")}
            </h2>
            {/* `Modal` owns the dismiss button now, so the header carries only
                the one action of its own. */}
            <Button
              kind="text-icon-left"
              variant="secondary"
              scale="compact"
              icon="Printer"
              text={t("selectors.teamQr.print")}
              isDisabled={qrCodes.length === 0}
              onPress={() => window.print()}
            />
          </div>
        }
      >
        {/* Same uncapped `Modal` body as the create and reset-PIN modals
            above. */}
        <div
          className="flex max-w-full flex-col overflow-y-auto"
          // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
          style={{ maxHeight: "calc(100vh - 320px)" }}
        >
          <p className="mb-16 text-size-13 text-on-surface-variant">
            {t("selectors.teamQr.intro")}
          </p>

          {qrQuery.isLoading ? (
            <Loader type="page" />
          ) : qrQuery.isError ? (
            <AppError
              description={t("selectors.teamQr.failed")}
              resolution={t("selectors.failureSupport")}
            />
          ) : qrCodes.length === 0 ? (
            <EmptyState heading={t("selectors.empty")} />
          ) : (
            // `repeat(auto-fill, minmax(160px, 1fr))` has no utility --
            // pallet says nothing about auto-fill track lists. These cards are
            // a fixed size (the 140px QR image plus its padding), so a
            // wrapping flex row shelves them the same way a grid would.
            <div className="flex flex-wrap gap-12">
              {qrCodes.map((qrCode) => (
                <div
                  key={qrCode.userId}
                  className="flex flex-col items-center gap-8 rounded-10 border border-outline bg-surface p-16"
                  data-test-role="qr-card"
                >
                  <img
                    className="block"
                    // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
                    style={{ width: 140, height: 140 }}
                    src={qrCode.dataUrl}
                    alt={t("selectors.teamQr.cardLabel", {
                      userId: qrCode.userId,
                    })}
                  />
                  <span className="font-mono text-size-13 font-strong text-on-surface">
                    {qrCode.userId}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

function SelectorTableRow({
  user,
  orgId,
  warehouseId,
  isTogglePending,
  onEdit,
  onResetPin,
  onToggle,
  t,
}: {
  user: AppUser;
  orgId: string;
  warehouseId: string;
  isTogglePending: boolean;
  onEdit: () => void;
  onResetPin: () => void;
  onToggle: () => void;
  t: TFunction;
}) {
  const settingsQuery = useSelectorSettings(orgId, warehouseId, user.user_id);
  const settings = settingsQuery.data ?? {};
  const zoneEligibility = decodeZoneEligibility(settings[ZONE_ELIGIBILITY_KEY]);

  return (
    <TableRow data-test-role="selector-row">
      <TableCell>
        <div className="flex items-center gap-12">
          <Avatar name={displayNameOf(user)} colorKey={user.user_id} />
          <span className="font-strong text-on-surface">
            {displayNameOf(user)}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <span className="font-mono font-strong text-on-surface">
          {user.user_id}
        </span>
      </TableCell>
      <TableCell>
        <Tag
          label={
            user.active
              ? t("selectors.status.active")
              : t("selectors.status.inactive")
          }
          display={{ tone: user.active ? "success" : "neutral" }}
        />
      </TableCell>
      <TableCell>
        {zoneEligibility.length > 0 ? (
          <span>{zoneEligibility.join(", ")}</span>
        ) : (
          <span className="text-on-surface-variant">—</span>
        )}
      </TableCell>
      <TableCell>
        {settings.group ? (
          <span>{settings.group}</span>
        ) : (
          <Tag
            label={t("selectors.table.notAssigned")}
            display={{ tone: "warning" }}
          />
        )}
      </TableCell>
      <TableCell>
        {settings.validation_level ? (
          <Badge
            label={settings.validation_level}
            color={{
              mode: "categorical",
              slot: validationSlot(settings.validation_level),
            }}
          />
        ) : (
          <span className="text-on-surface-variant">—</span>
        )}
      </TableCell>
      <TableCell>
        {settings[SHIFT_KEY] ? (
          <span>{settings[SHIFT_KEY]}</span>
        ) : (
          <span className="text-on-surface-variant">
            {t("selectors.table.shiftNotSet")}
          </span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex justify-end gap-8">
          <Button
            kind="icon-only"
            variant="tertiary"
            icon="Edit2"
            label={t("selectors.rowActions.edit")}
            onPress={onEdit}
            data-test-role="selector-edit"
          />
          <Button
            kind="text-only"
            variant="secondary"
            scale="compact"
            text={t("selectors.rowActions.resetPin")}
            onPress={onResetPin}
            data-test-role="selector-reset-pin"
          />
          <Button
            kind="text-only"
            variant={user.active ? "danger-container" : "success-container"}
            scale="compact"
            text={
              user.active
                ? t("selectors.rowActions.deactivate")
                : t("selectors.rowActions.activate")
            }
            isDisabled={isTogglePending}
            onPress={onToggle}
            data-test-role="selector-toggle-status"
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

export function RouteComponent() {
  const params = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const client = useMemo(() => apiClient(), []);
  const orgCfgClient = useOrgCfgFetchClient();
  const issClient = useIssApi();
  const queryClient = useQueryClient();

  // eslint-disable-next-line no-restricted-properties -- TODO: migrate to useAssertedTenancyContext()
  const orgId = AuthUtils.getOrgIdByStub(authState, params.org) ?? params.org;
  // eslint-disable-next-line no-restricted-properties -- TODO: migrate to useAssertedTenancyContext()
  const warehouseId = AuthUtils.getWarehouseId(
    authState,
    orgId,
    params.location,
  );
  const idsReady = typeof warehouseId === "string";

  // Zone eligibility is secondary to the selector directory itself, so a
  // failure here doesn't block the page -- the picker just renders with no
  // zones to offer (see `zoneNames` below).
  const zonesQuery = issClient.useQuery("get", "/zones/all", {});
  const zoneNames = ((zonesQuery.data?.data ?? []) as { name: string }[])
    .map((zone) => zone.name)
    .sort((a, b) => a.localeCompare(b));

  // Same treatment as `zoneNames` -- the `shift` dropdown is secondary to the
  // directory itself, so a failure here just leaves it with no options.
  const shiftNamesQuery = useWarehouseShiftNames(
    orgId,
    warehouseId ?? "",
    idsReady,
  );
  const shiftNames = shiftNamesQuery.data ?? [];

  const listQuery = client.useQuery(
    "get",
    "/app_user/list",
    {
      params: {
        query: {
          org_id: orgId,
          warehouse_id: warehouseId ?? "",
          app_name: APP_NAME,
        },
      },
    },
    { enabled: idsReady },
  );

  const [qrOpen, setQrOpen] = useState(false);

  const createMutation = client.useMutation("post", "/app_user/create");
  const updateMutation = client.useMutation("post", "/app_user/update");
  const resetPinMutation = client.useMutation("post", "/app_user/reset_pin");

  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<AppUser | null>(null);
  const [editTarget, setEditTarget] = useState<AppUser | null>(null);
  const [statusPendingId, setStatusPendingId] = useState<number | null>(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const users = (listQuery.data ?? []) as AppUser[];
  const trimmed = q.trim().toLowerCase();
  const filtered = trimmed
    ? users.filter((u) => u.user_id.toLowerCase().includes(trimmed))
    : users;
  const sorted = [...filtered].sort((a, b) =>
    a.user_id.localeCompare(b.user_id),
  );

  const openTeamQr = () => setQrOpen(true);

  const closeTeamQr = () => setQrOpen(false);

  const handleCreate = async (
    userId: string,
    pin: string,
    extras: CreateSelectorExtras,
  ) => {
    try {
      await createMutation.mutateAsync({
        body: {
          app_name: APP_NAME,
          org_id: orgId,
          warehouse_id: warehouseId!,
          user_id: userId,
          pin,
          ...(extras.name ? { user_name: extras.name } : {}),
        },
      });
      logger.info("Created selector", { userId });
    } catch (error) {
      logger.warn("Failed to create selector", { error, userId });
      toast.error(errorMessage(error, t("selectors.create.failedFallback")));
      throw error;
    }

    // Best-effort follow-ups: the account already exists at this point, so a
    // failure here shouldn't reopen the form or block the PIN reveal -- just
    // warn, since the admin can still fix status/settings from the row and
    // the edit drawer afterward.
    if (!extras.active) {
      try {
        await updateMutation.mutateAsync({
          body: {
            app_name: APP_NAME,
            org_id: orgId,
            warehouse_id: warehouseId!,
            user_id: userId,
            active: false,
          },
        });
      } catch (error) {
        logger.warn("Failed to deactivate newly created selector", {
          error,
          userId,
        });
        toast.error(t("selectors.create.statusFailed", { userId }));
      }
    }

    try {
      const { error } = await orgCfgClient.POST("/orgs/user_settings/add", {
        body: {
          orgId,
          warehouseId: warehouseId!,
          userId,
          settings: extras.settings,
        },
      });
      if (error) throw error;
    } catch (error) {
      logger.warn("Failed to save new selector's settings", {
        error,
        userId,
      });
      toast.error(t("selectors.create.settingsFailed", { userId }));
    }

    listQuery.refetch();
  };

  const handleResetPin = async (pin: string) => {
    if (!resetTarget) return;
    const userId = resetTarget.user_id;
    try {
      await resetPinMutation.mutateAsync({
        body: {
          app_name: APP_NAME,
          org_id: orgId,
          warehouse_id: warehouseId!,
          user_id: userId,
          new_pin: pin,
        },
      });
      logger.info("Reset selector PIN", { userId });
    } catch (error) {
      logger.warn("Failed to reset selector PIN", { error, userId });
      toast.error(errorMessage(error, t("selectors.resetPin.failedFallback")));
      throw error;
    }
  };

  const handleToggleStatus = (user: AppUser) => {
    if (updateMutation.isPending) return;
    const nextActive = !user.active;

    setStatusPendingId(user.id);
    updateMutation
      .mutateAsync({
        body: {
          app_name: APP_NAME,
          org_id: orgId,
          warehouse_id: warehouseId!,
          user_id: user.user_id,
          active: nextActive,
        },
      })
      .then(() => {
        logger.info("Updated selector status", {
          userId: user.user_id,
          active: nextActive,
        });
        toast.success(
          t(
            nextActive
              ? "selectors.toast.activated"
              : "selectors.toast.deactivated",
            { userId: user.user_id },
          ),
        );
        listQuery.refetch();
      })
      .catch((error: unknown) => {
        logger.warn("Failed to update selector status", {
          error,
          userId: user.user_id,
        });
        toast.error(
          errorMessage(
            error,
            t("selectors.toast.statusFailed", { userId: user.user_id }),
          ),
        );
      })
      .finally(() => setStatusPendingId(null));
  };

  const handleSaveSettings = async (
    userId: string,
    patch: Record<string, string>,
  ) => {
    setIsSavingSettings(true);
    try {
      await saveSelectorSettings(
        orgCfgClient,
        orgId,
        warehouseId!,
        userId,
        patch,
      );
      logger.info("Updated selector settings", { userId });
      toast.success(t("selectors.detail.saveSuccessToast", { userId }));
      queryClient.invalidateQueries({
        queryKey: ["selector-settings", orgId, warehouseId, userId],
      });
      setEditTarget(null);
    } catch (error) {
      logger.warn("Failed to update selector settings", { error, userId });
      toast.error(
        errorMessage(error, t("selectors.detail.saveFailedFallback")),
      );
      throw error;
    } finally {
      setIsSavingSettings(false);
    }
  };

  return (
    <div className="bg-surface h-full w-full">
      <div className="page-shell">
        <header className="page-header">
          <div className="page-header-left">
            <h1 className="page-title">{t("selectors.heading")}</h1>
            <p className="page-subtitle">{t("selectors.subheading")}</p>
          </div>
          <div className="page-header-right">
            <div className="page-header-searches">
              <Search
                aria-label={t("selectors.filters.searchAriaLabel")}
                placeholder={t("selectors.filters.searchPlaceholder")}
                value={q}
                onChange={(value) => setQ(String(value))}
                onClear={() => setQ("")}
              />
            </div>
            <div className="page-header-actions">
              <Button
                kind="text-icon-left"
                variant="secondary"
                icon="Grid"
                text={t("selectors.teamQr.button")}
                onPress={openTeamQr}
              />

              <Button
                kind="text-icon-left"
                variant="avatar-surface"
                icon="Plus"
                text={t("selectors.createButton")}
                onPress={() => setCreateOpen(true)}
              />
            </div>
          </div>
        </header>

        <div className="table-frame">
          {!idsReady || listQuery.isLoading ? (
            <Loader type="page" />
          ) : listQuery.isError ? (
            <AppError
              description={t("selectors.failed")}
              resolution={t("selectors.failureSupport")}
            />
          ) : (
            <Table
              aria-label={t("selectors.table.ariaLabel")}
              isResizable={false}
            >
              <TableHead>
                <TableHeaderCell isRowHeader={true}>
                  {t("selectors.table.user")}
                </TableHeaderCell>
                <TableHeaderCell>{t("selectors.table.userId")}</TableHeaderCell>
                <TableHeaderCell>{t("selectors.table.status")}</TableHeaderCell>
                <TableHeaderCell>
                  {t("selectors.table.zoneEligibility")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("selectors.table.userGroup")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("selectors.table.validation")}
                </TableHeaderCell>
                <TableHeaderCell>{t("selectors.table.shift")}</TableHeaderCell>
                <TableHeaderCell>
                  {t("selectors.table.actions")}
                </TableHeaderCell>
              </TableHead>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <EmptyState heading={t("selectors.empty")} />
                    </TableCell>
                  </TableRow>
                ) : (
                  sorted.map((user) => (
                    <SelectorTableRow
                      key={user.id}
                      user={user}
                      orgId={orgId}
                      warehouseId={warehouseId ?? ""}
                      isTogglePending={statusPendingId === user.id}
                      onEdit={() => setEditTarget(user)}
                      onResetPin={() => setResetTarget(user)}
                      onToggle={() => handleToggleStatus(user)}
                      t={t}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <CreateSelectorModal
        isOpen={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={handleCreate}
        isPending={createMutation.isPending}
        zoneNames={zoneNames}
        shiftNames={shiftNames}
        t={t}
      />

      <ResetPinModal
        user={resetTarget}
        onOpenChange={(open) => {
          if (!open) setResetTarget(null);
        }}
        onSubmit={handleResetPin}
        isPending={resetPinMutation.isPending}
        t={t}
      />

      <TeamQrCodesModal
        isOpen={qrOpen}
        onClose={closeTeamQr}
        orgId={orgId}
        warehouseId={warehouseId ?? ""}
        t={t}
      />

      {editTarget ? (
        <SelectorDetailDrawer
          user={editTarget}
          orgId={orgId}
          warehouseId={warehouseId ?? ""}
          zoneNames={zoneNames}
          shiftNames={shiftNames}
          onClose={() => setEditTarget(null)}
          onSave={handleSaveSettings}
          isSaving={isSavingSettings}
          t={t}
        />
      ) : null}
    </div>
  );
}
